import { Drawer, Modal } from '@mantine/core'
import { useDisclosure, useMediaQuery } from '@mantine/hooks'
import { useProfileFields } from './api'
import { useEffect, useState, useMemo } from 'react'
import { useUserData } from '../auth/api'
import { useLocation } from 'react-router'
import { useUpdateSettings, useSettings } from '../settings/api'
import { useUploadAsset } from '../assets/api'
import OnboardingContent from './OnboardingContent'
import classes from './Onboarding.module.css'

export default function Onboarding() {
  const location = useLocation()
  const isDesktop = useMediaQuery('(min-width: 800px)')
  const { data: user } = useUserData(location.pathname)
  const { data: profileFields } = useProfileFields()
  const { data: settings } = useSettings()
  const { mutate: updateSettings, isPending: isUpdatingSettings } =
    useUpdateSettings()
  const { mutate: uploadAsset, isPending: isUploadingAsset } = useUploadAsset()
  const [opened, { close }] = useDisclosure(true)
  const [currentStep, setCurrentStep] = useState(1)
  const totalSteps = 3
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null)
  const [profileSuccess, setProfileSuccess] = useState<string | null>(null)
  const [profileError, setProfileError] = useState<string | null>(null)
  const [fieldValues, setFieldValues] = useState<
    Record<string, string | number | boolean>
  >({})
  const [fieldErrors, setFieldErrors] = useState<Set<string>>(new Set())

  const sortedProfileFields = useMemo(
    () => [...profileFields].sort((a, b) => a.order - b.order),
    [profileFields]
  )

  useEffect(() => {
    if (user.photo_url) {
      setAvatarPreview(user.photo_url)
    }
  }, [user])

  useEffect(() => {
    if (settings.fields) {
      const initialValues: Record<string, string | number | boolean> = {}
      Object.entries(settings.fields).forEach(([key, value]) => {
        if (
          typeof value === 'string' ||
          typeof value === 'number' ||
          typeof value === 'boolean'
        ) {
          initialValues[key] = value
        }
      })
      setFieldValues(initialValues)
    }
  }, [settings.fields])

  const handleNext = () => {
    if (currentStep < totalSteps) {
      setCurrentStep((prev) => prev + 1)
    }
  }

  const handleAvatarUpload = (file: File | null) => {
    if (!file) return

    const reader = new FileReader()
    reader.onloadend = () => {
      setAvatarPreview(reader.result as string)
    }
    reader.readAsDataURL(file)

    uploadAsset(
      { file, model_field: 'account.Profile.photo' },
      {
        onSuccess: (assetId) => {
          const profileData = {
            photo_id: assetId,
          }

          updateSettings(profileData, {
            onSuccess: () => {
              setProfileSuccess('Profile photo update successfully.')
            },
          })
        },
        onError: () => {
          setProfileError('Failed to upload profile photo.')
          setAvatarPreview(null)
        },
      }
    )
  }

  const handleFieldChange = (key: string, value: string | number | boolean) => {
    setFieldValues((prev) => ({
      ...prev,
      [key]: value,
    }))
    if (fieldErrors.has(key)) {
      setFieldErrors((prev) => {
        const newErrors = new Set(prev)
        newErrors.delete(key)
        return newErrors
      })
    }
  }

  const handleFinish = () => {
    const errors = new Set<string>()
    sortedProfileFields.forEach((field) => {
      if (field.is_required) {
        const value = fieldValues[field.key]
        if (value === undefined || value === null || value === '') {
          errors.add(field.key)
        }
      }
    })

    if (errors.size > 0) {
      setFieldErrors(errors)
      return
    }

    setFieldErrors(new Set())
    setProfileError(null)

    const filteredFields = Object.entries(fieldValues).reduce(
      (acc, [key, value]) => {
        if (value !== '') {
          acc[key] = value
        }
        return acc
      },
      {} as Record<string, string | number | boolean>
    )

    updateSettings(
      { fields: filteredFields },
      {
        onSuccess: () => {
          close()
        },
        onError: () => {
          setProfileError('Failed to update profile fields.')
        },
      }
    )
  }

  return (
    <>
      {isDesktop ? (
        <Modal.Root
          opened={opened}
          onClose={close}
          centered
          closeOnClickOutside={false}
          closeOnEscape={false}
          transitionProps={{
            transition: 'fade',
            duration: 200,
            exitDuration: 150,
            timingFunction: 'ease-out',
          }}
          classNames={{
            body: classes.body,
            content: classes.content,
          }}
        >
          <Modal.Overlay backgroundOpacity={0.3} blur={2} />
          <Modal.Content>
            <Modal.Body>
              <OnboardingContent
                currentStep={currentStep}
                totalSteps={totalSteps}
                avatarPreview={avatarPreview}
                isUploadingAsset={isUploadingAsset}
                handleAvatarUpload={handleAvatarUpload}
                profileSuccess={profileSuccess}
                profileError={profileError}
                setProfileSuccess={setProfileSuccess}
                setProfileError={setProfileError}
                handleNext={handleNext}
                sortedProfileFields={sortedProfileFields}
                fieldValues={fieldValues}
                handleFieldChange={handleFieldChange}
                fieldErrors={fieldErrors}
                isUpdatingSettings={isUpdatingSettings}
                handleFinish={handleFinish}
              />
            </Modal.Body>
          </Modal.Content>
        </Modal.Root>
      ) : (
        <Drawer.Root
          opened={opened}
          onClose={close}
          position="bottom"
          closeOnClickOutside={false}
          closeOnEscape={false}
          classNames={{
            body: classes.body,
            content: classes.content,
          }}
          transitionProps={{
            transition: 'fade',
            duration: 200,
            exitDuration: 150,
            timingFunction: 'ease-out',
          }}
        >
          <Drawer.Overlay backgroundOpacity={0.3} blur={2} />
          <Drawer.Content>
            <Drawer.Body>
              <OnboardingContent
                currentStep={currentStep}
                totalSteps={totalSteps}
                avatarPreview={avatarPreview}
                isUploadingAsset={isUploadingAsset}
                handleAvatarUpload={handleAvatarUpload}
                profileSuccess={profileSuccess}
                profileError={profileError}
                setProfileSuccess={setProfileSuccess}
                setProfileError={setProfileError}
                handleNext={handleNext}
                sortedProfileFields={sortedProfileFields}
                fieldValues={fieldValues}
                handleFieldChange={handleFieldChange}
                fieldErrors={fieldErrors}
                isUpdatingSettings={isUpdatingSettings}
                handleFinish={handleFinish}
              />
            </Drawer.Body>
          </Drawer.Content>
        </Drawer.Root>
      )}
    </>
  )
}
